import type { RecordCorrectionDto } from '@repo/api-client';

/**
 * The four reasons a correction may state, and what each of them does to a
 * level.
 *
 * **The direction is the reason's, and it is the whole safety property of this
 * screen.** `quantity` on the wire is a positive magnitude and the server
 * derives the sign from `reason` — `BREAKAGE`, `SPOILAGE` and `LOSS` remove
 * goods, `FOUND` adds them. So the form never asks anybody for a signed number,
 * and the only place the direction is stated is beside the reason they picked.
 *
 * The API is explicit about why it is built that way: two ways of saying "two
 * fewer" — `-2` under `FOUND` and `2` under `BREAKAGE` — is how a sign error
 * becomes a level nobody can explain. A minus typed into a quantity box would be
 * refused `422 STOCK_QUANTITY_INVALID`; the form refuses it a step earlier, by
 * never being a box a minus fits in (`correction-form.ts`).
 *
 * **The labels and the icons are not restated here.** `movement-reasons.ts`
 * beside this file already names all eight movement reasons for the log, and
 * these four are a subset of them — a correction is read back as the movements
 * it wrote, so a form that called it "Broken" while the log called it something
 * else would be two names for one row.
 */

/** The published set, straight off the generated body type rather than restated. */
export type CorrectionReason = RecordCorrectionDto['reason'];

/** Which way a reason moves a level. Two values, because there are two ways. */
export type CorrectionDirection = 'removes' | 'adds';

/**
 * The four, in the order the picker offers them: the three that remove goods,
 * then the one that adds them.
 *
 * Grouped that way rather than alphabetically because the direction is the
 * distinction somebody is actually making, and `FOUND` sitting apart from the
 * others is the shape of the choice. Within the removals the order is the one
 * the API's own enum uses.
 *
 * A `const` tuple rather than a plain array, because `correction-form.ts` builds
 * the form's `z.enum` straight off it — so the schema, the picker and the body's
 * type are one list, and a reason the picker offers is a reason the body admits
 * by construction.
 *
 * **A fifth reason cannot be added to the API without this file failing to
 * compile**, which is the point of deriving the type above: `directionOf` is an
 * exhaustive switch with a stated return type, so a widened union leaves it with
 * a path that returns nothing and TypeScript refuses it. Whoever fixes that
 * function is standing in the file that also holds this list.
 */
export const CORRECTION_REASONS = [
  'BREAKAGE',
  'SPOILAGE',
  'LOSS',
  'FOUND',
] as const satisfies readonly CorrectionReason[];

/**
 * Whether a string the form is holding is one of the four.
 *
 * A guard rather than a cast, and it earns its place twice: the form's `reason`
 * field is a `string` because "nothing chosen yet" is `''`, and this is what
 * narrows it to a reason on the way into the body — so no `as` is written
 * anywhere between the picker and the wire. The widening on the left is the
 * inverse: `includes` on a `const` tuple will not accept an arbitrary string,
 * and reading the tuple as `readonly string[]` is safe in the direction that
 * loses information rather than the one that invents it.
 */
export function isCorrectionReason(value: string): value is CorrectionReason {
  return (CORRECTION_REASONS as readonly string[]).includes(value);
}

/** Whether this reason takes goods off the shelf or puts them on it. */
export function directionOf(reason: CorrectionReason): CorrectionDirection {
  switch (reason) {
    case 'BREAKAGE':
    case 'SPOILAGE':
    case 'LOSS':
      return 'removes';
    case 'FOUND':
      return 'adds';
  }
}

/**
 * What the reason will do, in the words the form says it in.
 *
 * Short enough to sit beside an option in the picker and to be repeated under
 * it once one is chosen, because the person who most needs to read it is the one
 * who has already picked and is now typing a number.
 */
export function directionSays(reason: CorrectionReason): string {
  return directionOf(reason) === 'removes' ? 'takes goods off the shelf' : 'puts goods on it';
}

/**
 * The sentence under the quantity fields once a reason is chosen.
 *
 * It states the direction and then says the thing the API asks every client to
 * say: the count is plain. Nobody types a minus, and a form that left them to
 * infer that from a refusal would be a form that earns the refusal.
 */
export function quantitySays(reason: CorrectionReason): string {
  return directionOf(reason) === 'removes'
    ? 'How many were lost to the shelf — as a plain count, never a minus sign. The reason is what takes them off.'
    : 'How many turned up — as a plain count. The reason is what puts them back on.';
}
